'use client';

import {
    clsx,
    getStoreDisplayTime,
    storeIsOpen,
    usePageLayout,
    PageType,
    useCart,
    getSearchStores,
    useStore,
    useStoreDispatch,
} from '@libs/common-next';
import type { SearchStore } from '@libs/common-types';
import { DeliveryType, getLocale, NAVIGATION_URLS } from '@libs/core';
import { List, StoreListingCard, StoreSearchDrawer } from '@petsmart-ui/sparky';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UpdateItemFulfillmentMethodRequestBody } from '@libs/common-node';

import type { StoreDrawerProps } from './types';
import {
    convertStoreIdToDemandwareFormat,
    getAlertText,
    storeSearchHasStock,
    getUserLocation,
    enhanceStoreListWithInventory,
    useSearchDrawerSku,
    filterStoreListToShow,
} from './utils';

import './StoreDrawer.scss';

export default function StoreDrawer({
    align,
    drawerHeading,
    showStock,
    toggleText,
    className,
    pageType,
}: StoreDrawerProps) {
    const {
        setStoreDrawerOpen,
        setFullStoreListResults,
        fullStoreListResults,
        storeDrawerOpen,
        setCartStoreDrawerOpen,
        cartStoreDrawerOpen,
        currentVariantId,
        currentBopisMinimumCaseQty,
        setContactUsFormStore,
    } = usePageLayout();
    const { updateItemFulfillmentMethod } = useCart();
    const {
        selectedStore: { data: store },
    } = useStore();
    const { updateSelectedStore } = useStoreDispatch();
    const sku = useSearchDrawerSku(pageType);
    const [isLoading, setIsLoading] = useState(false);
    const [ranIPAutoSearch, setRanIPAutoSearch] = useState(false);
    const [showInventoryOnly, setShowInventoryOnly] = useState<boolean>(false);
    const [searchBoxText, setSearchBoxText] = useState<string | null>(null);
    const [pickedStore, setPickedStore] = useState<number | null>(store?.StoreNumber ?? null);

    const componentClassName = clsx('store-drawer', className);
    const isCPC = pageType === PageType.CART;
    const storeListResultsToShow = useMemo(
        () => filterStoreListToShow(fullStoreListResults, showInventoryOnly),
        [fullStoreListResults, showInventoryOnly]
    );
    const showAlert = !isCPC && isLoading && storeListResultsToShow.length === 0;
    const includeAttributes = Boolean(sku);
    const allStoresURL = NAVIGATION_URLS.legacy.stores;

    const onToggleHasItems = () => {
        setShowInventoryOnly(!showInventoryOnly);
    };

    const resetResults = () => {
        setIsLoading(true);
        setPickedStore(null);
    };

    const finishLoadingStores = useCallback(
        async (storeList: SearchStore[]) => {
            // Only show items
            const filteredStoreList = includeAttributes
                ? storeList?.filter(storeItem => storeItem.attributes?.bopisEnabled)
                : storeList;
            const enhancedStoreList = await enhanceStoreListWithInventory(
                filteredStoreList,
                sku,
                currentBopisMinimumCaseQty
            );

            const atLeastOneInStock = Boolean(enhancedStoreList?.some(storeSearchHasStock));

            setIsLoading(false);
            setShowInventoryOnly(atLeastOneInStock);
            setFullStoreListResults(enhancedStoreList);
        },
        [currentBopisMinimumCaseQty, setFullStoreListResults, sku]
    );

    const doStoreSearch = useCallback(
        async ({ lat, lng, query }: { lat?: number; lng?: number; query?: string }) => {
            resetResults();

            const storeList = await getSearchStores({
                lat,
                lng,
                query,
                radius: 300000,
                includeAttributes,
            });

            await finishLoadingStores(storeList);
        },
        [finishLoadingStores, includeAttributes]
    );

    const handleOnSearch = useCallback(async () => {
        if (searchBoxText) {
            await doStoreSearch({ query: searchBoxText });
        }
    }, [searchBoxText, doStoreSearch]);

    const handleUseMyLocation = async () => {
        const { isGeolocationEnabled, latitude: lat, longitude: lng } = await getUserLocation();

        if (!isGeolocationEnabled && ranIPAutoSearch) {
            // TODO: handle case where user denies location data but calls myLocationSearch
            return;
        }

        setSearchBoxText(null);
        await doStoreSearch({ lat, lng });
    };

    const closeDrawer = useCallback(() => {
        setStoreDrawerOpen(null);
        setCartStoreDrawerOpen(prevState => ({
            ...prevState,
            open: false,
        }));
    }, []);

    const handleSelectStoreCPC = async (store: number) => {
        const pickedStoreDemandwareFormat = convertStoreIdToDemandwareFormat(store);
        const requestBodyForChangeStoreCPC: UpdateItemFulfillmentMethodRequestBody = {
            fulfillmentMethod: DeliveryType.BOPIS,
            storeId: pickedStoreDemandwareFormat,
            itemId: cartStoreDrawerOpen.item?.id ?? '',
        };
        await updateItemFulfillmentMethod(requestBodyForChangeStoreCPC);
        closeDrawer();
    };

    const handleSelectStoreContactUs = async (store: number) => {
        const updatedStore = fullStoreListResults.find(s => s.Store.StoreNumber === store)?.Store;
        setContactUsFormStore(updatedStore);
        closeDrawer();
    };

    const onDrawerSelectStore = async (pickedStoreParam?: number) => {
        // if param is sent, use that one
        // otherwise use pickedStore state
        const selectedStore = pickedStoreParam ?? pickedStore;
        if (!selectedStore) {
            return;
        }

        if (isCPC && cartStoreDrawerOpen.item) {
            return handleSelectStoreCPC(selectedStore);
        }

        if (storeDrawerOpen?.pageType === 'contactUs') {
            return handleSelectStoreContactUs(selectedStore);
        }

        const updatedStore = fullStoreListResults.find(s => s.Store.StoreNumber === selectedStore)?.Store;
        const updatedStoreId = updatedStore?.StoreNumber;
        if (updatedStoreId) {
            updateSelectedStore(updatedStoreId);
        }
        closeDrawer();
    };

    const getInitialStoreList = useCallback(async () => {
        let storeList: SearchStore[] = [];
        if (!ranIPAutoSearch) {
            storeList = await getSearchStores({
                includeAttributes: true,
            });
            setRanIPAutoSearch(true);
        }

        return storeList;
    }, [ranIPAutoSearch]);

    useEffect(() => {
        if (store?.StoreNumber) {
            setPickedStore(store.StoreNumber);
        }
    }, [store?.StoreNumber]);

    // Update the store list with fresh inventory data when sku is updated
    useEffect(() => {
        finishLoadingStores(fullStoreListResults);
    }, [sku]);

    useEffect(() => {
        // if drawer is opened and has at least one store in list that has stock, only show those stores
        if (Boolean(storeDrawerOpen) || cartStoreDrawerOpen.open) {
            const atLeastOneInStock = storeListResultsToShow.some(storeSearchHasStock);

            setShowInventoryOnly(atLeastOneInStock);

            if (!atLeastOneInStock) {
                handleUseMyLocation();
            }
        }
    }, [storeDrawerOpen, cartStoreDrawerOpen.open, sku]);

    useEffect(() => {
        // PDP only code. !fullStoreListResults.length and showStock are falsy on PLP
        if (!fullStoreListResults.length && showStock && currentVariantId && storeDrawerOpen) {
            setIsLoading(true);
            getInitialStoreList().then(async storeList => {
                if (!storeList?.length) {
                    return;
                }
                finishLoadingStores(storeList);
            });
        }
    }, [
        currentBopisMinimumCaseQty,
        currentVariantId,
        finishLoadingStores,
        fullStoreListResults.length,
        getInitialStoreList,
        setFullStoreListResults,
        showStock,
        sku,
        storeDrawerOpen,
    ]);

    useEffect(() => {
        // PLP only code
        if (!fullStoreListResults.length && !showStock) {
            getInitialStoreList().then(storeList => {
                if (!storeList?.length) {
                    return;
                }

                setFullStoreListResults(storeList);

                if (!store) {
                    setPickedStore(storeList[0].Store.StoreNumber);
                } else {
                    setPickedStore(store.StoreNumber);
                }
            });
        }
    }, [
        fullStoreListResults,
        showStock,
        currentVariantId,
        store,
        setPickedStore,
        setFullStoreListResults,
        getInitialStoreList,
    ]);

    const renderStoreCard = (s: SearchStore) => {
        const { HasCurrentProductInStock: hasInventory } = s.Store;

        const timezonedDate = new Date().toLocaleString('en', {
            timeZone: s.Store.TimeZone?.TimeZoneId,
        });

        // date we are comparing to uses the timezone of the store we are using
        const currentDate = new Date(timezonedDate);

        return (
            <List.Item
                key={s.Store.StoreNumber}
                onClick={() => {
                    setPickedStore(s.Store.StoreNumber);
                }}
            >
                <StoreListingCard
                    data-testid={`store-test-${s.Store.StoreNumber}`}
                    address={{
                        addressName: s.Store.Name,
                        street: s.Store.StreetLine1,
                        city: s.Store.City,
                        state: s.Store.StateProvinceAbbreviation,
                        postalCode: s.Store.ZipPostalCode,
                    }}
                    stockStatus={s.Store.CurrentProductStockstatus}
                    showStock={showStock}
                    selected={pickedStore === s.Store.StoreNumber}
                    hasRadio
                    hasItems={hasInventory}
                    rating={undefined}
                    openText={getStoreDisplayTime(s.Store, currentDate)}
                    distance={
                        s.DistanceToStoreFromOrigin > 0
                            ? `${s.DistanceToStoreFromOrigin.toFixed(1)} ${s.UnitOfDistance.replace(
                                  'kilometers',
                                  'kilometres'
                              )}`
                            : undefined
                    }
                    closed={!storeIsOpen(s.Store, currentDate)}
                    myStore={store?.StoreNumber === s.Store.StoreNumber}
                />
            </List.Item>
        );
    };

    const alertContent = getAlertText(searchBoxText ?? '');

    return (
        <StoreSearchDrawer
            viewStoresLink={allStoresURL}
            align={align}
            data-testid="store-drawer"
            className={componentClassName}
            isActive={Boolean(storeDrawerOpen) || cartStoreDrawerOpen.open}
            onClose={() => {
                setStoreDrawerOpen(null);
                setCartStoreDrawerOpen(prevState => ({
                    ...prevState,
                    open: false,
                }));
            }}
            showAlert={showAlert && !isLoading}
            showList={!showAlert && !isLoading}
            showStoreConfirmButton={pickedStore !== null}
            onUseMyLocation={handleUseMyLocation}
            onSearch={handleOnSearch}
            toggleText={toggleText}
            onSelectStore={() => onDrawerSelectStore()}
            isLoading={isLoading}
            hasItems={Boolean(showInventoryOnly)}
            drawerHeading={drawerHeading}
            alertIcon="exclamation-circle-fill"
            alertHeading={alertContent?.title ?? 'No locations found'}
            placeholder={getLocale() === 'en-CA' ? 'City or postal code' : 'City, state or zip code'}
            alertText={
                alertContent?.text ? (
                    <>
                        {alertContent?.text}
                        <a href={alertContent?.link} target="_blank" rel="noreferrer">
                            {alertContent?.linkText}
                        </a>
                    </>
                ) : (
                    ''
                )
            }
            onToggleHasItems={onToggleHasItems}
            onSearchBoxChange={(val: string) => setSearchBoxText(val)}
            value={searchBoxText ?? ''}
            disableStoreConfirmButton={storeListResultsToShow.length === 0 || isLoading}
        >
            <List data-testid="store-drawer-list">{storeListResultsToShow.map(renderStoreCard)}</List>
        </StoreSearchDrawer>
    );
}
